import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Stack,
  Typography,
  Checkbox,
  Button,
  LinearProgress,
  Tooltip,
  Collapse,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BuildingIcon } from "@/common/icons";
import { useForm } from "react-hook-form";
import { resolveSearchControls } from "@/components/ListToolbar/searchControls";
import {
  CustomSectionTitle,
  CustomLoading,
  CustomChip,
  CustomTextInput,
  CustomButton,
  CustomSummaryCard,
  PreviewDrawer,
  colors,
} from "@ippon/shared-components";
import { formatSignedYen, formatYen } from "@ippon/shared-utils";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBudgetAnalysis,
  type BudgetAnalysisProperty,
  type BudgetAnalysisMonthly,
} from "@/store/budgetAnalysisSlice";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorType } from "@/utils/apiErrorType";
import type { ApiErrorPayload } from "@/utils/apiErrorPayload";
import {
  aggregateMonthly,
  aggregateComparableNoi,
  aggregateNoiAvailability,
  aggregateTotals,
  pickMetric,
  computeMonthProgress,
  buildPortfolioChartData,
  resolveFinancialValueTone,
  type Metric,
} from "./budgetAnalysisAgg";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";

// ── 型・定数 ──────────────────────────────────────────

type GroupBy = "group" | "custom";

const MONTH_LABELS: Record<string, string> = {
  "01": "1月",
  "02": "2月",
  "03": "3月",
  "04": "4月",
  "05": "5月",
  "06": "6月",
  "07": "7月",
  "08": "8月",
  "09": "9月",
  "10": "10月",
  "11": "11月",
  "12": "12月",
};

// 青〜ティール系で 10 グループまで判別できるよう濃淡＋色相を段階的に変化
// 全チャート共通: 予算/実績の2色
const CHART_BUDGET_COLOR = "#DBF8FF";
const CHART_ACTUAL_COLOR = "#0B9DBD";
// 今月マーカー (PPT でよく引く今月の縦線相当)。予算/実績のティールと区別するためアンバー系
const CHART_TODAY_COLOR = "#F5A623";
// ホバー時の軸ハイライト線。既定の黒 (#000) をグレーに
const CHART_HIGHLIGHT_COLOR = "#9E9E9E";

// カード等で色が必要な箇所用
const SERIES_COLORS = ["#0B9DBD", "#0B9DBC", "#DBF8FF"];
// グループサマリーのスパークラインは全グループ同色 (薄色パレット循環で線が見えなくなるのを防ぐ)
const SPARKLINE_COLOR = "#0B9DBD";

// 自前凡例（全チャート共通）
function ChartLegend() {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: 1,
            backgroundColor: CHART_BUDGET_COLOR,
            border: "1px solid #E0E0E0",
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#666" }}>予算</Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: 1,
            backgroundColor: CHART_ACTUAL_COLOR,
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#666" }}>実績</Typography>
      </Stack>
    </Stack>
  );
}

/** 金額表示（0 は ¥0） */
const fmtZero = formatYen;

const financialValueColor = (value: number, available = true): string => {
  const tone = resolveFinancialValueTone(value, available);
  if (tone === "negative") return colors.status.error;
  if (tone === "unavailable") return colors.text.secondary;
  return colors.text.primary;
};

const _fmtShort = (n: number): string => {
  if (n === 0) return "-";
  if (Math.abs(n) >= 100_000_000) return `${(n / 100_000_000).toFixed(1)}億`;
  if (Math.abs(n) >= 10_000) return `${Math.round(n / 10_000)}万`;
  return n.toLocaleString("ja-JP");
};
void _fmtShort;

// チャート専用 (軸ラベル/Tooltip): 0 は「0」と表示する
const fmtChart = (n: number | null | undefined): string => {
  const v = n ?? 0;
  if (v === 0) return "0";
  if (Math.abs(v) >= 100_000_000) return `${(v / 100_000_000).toFixed(1)}億`;
  if (Math.abs(v) >= 10_000) return `${Math.round(v / 10_000)}万`;
  return v.toLocaleString("ja-JP");
};

const COLLAPSED_VISIBLE = 6;

// カスタムツールチップ（丸ぽちなし・12px）
function ChartTooltipContent({ series, dataIndex, axisValue }: any) {
  if (dataIndex == null) return null;
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #E0E0E0",
        borderRadius: "4px",
        p: 1,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        minWidth: 120,
      }}
    >
      {axisValue != null && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
          {String(axisValue)}
        </Typography>
      )}
      {series.map(({ id, label, valueFormatter, data, color }: any) => {
        const val = valueFormatter?.(data[dataIndex]) ?? data[dataIndex];
        if (val == null) return null;
        return (
          <Stack
            key={id}
            direction="row"
            spacing={1.5}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 8,
                  height: 3,
                  borderRadius: 1,
                  backgroundColor: color,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}
              >
                {label}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
                color:
                  Number(data[dataIndex] ?? 0) < 0
                    ? colors.status.error
                    : undefined,
              }}
            >
              {val}
            </Typography>
          </Stack>
        );
      })}
    </Box>
  );
}

// ── Mini Sparkline ────────────────────────────────────

const MiniSparkline = React.memo(function MiniSparkline({
  data,
  color,
}: {
  data: number[];
  color: string;
}) {
  const w = 80;
  const h = 24;
  const pad = 2;
  const values = (data || []).filter((v) => Number.isFinite(v));
  const svgBaseProps = {
    width: w,
    height: h,
    viewBox: `0 0 ${w} ${h}`,
    preserveAspectRatio: "none" as const,
    style: { flexShrink: 0, display: "block" },
  };
  if (values.length === 0) {
    const y = h / 2;
    return (
      <svg {...svgBaseProps}>
        <polyline
          points={`0,${y} ${w},${y}`}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // データなし / 1点 / 全て同値 → 中央に平坦な水平線を引く
  // (動きを表せない系列で右下がり等の誤解を生まないようにする)
  const isFlat = values.length <= 1 || values.every((v) => v === values[0]);
  if (isFlat) {
    const y = h / 2;
    return (
      <svg {...svgBaseProps}>
        <polyline
          points={`0,${y} ${w},${y}`}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const denom = values.length > 1 ? values.length - 1 : 1;
  const points = values
    .map(
      (v, i) =>
        `${(i / denom) * w},${h - pad - ((v - min) / range) * (h - pad * 2)}`,
    )
    .join(" ");
  return (
    <svg {...svgBaseProps}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

// ── 物件別 NOI 内訳 ────────────────────────────────────

export const PropertyBreakdown = React.memo(function PropertyBreakdown({
  groupProperties,
  monthKeys,
  completedCount,
}: {
  groupProperties: BudgetAnalysisProperty[];
  monthKeys: string[];
  completedCount: number;
}) {
  const navigate = useNavigate();
  void monthKeys;

  const items = useMemo(
    () =>
      [...groupProperties]
        .map((p) => {
          // 全月そのまま合算 (ゲートなし)。予算未入力でも実績は反映される。
          const t = aggregateTotals([p], completedCount);
          const noiActual = t.noiActual;
          const noiBudget = t.noiBudget;
          const noiDiff = noiActual - noiBudget;
          const availability = aggregateNoiAvailability([p], completedCount);
          const achievementRate =
            t.incomeBudget > 0 ? (t.incomeActual / t.incomeBudget) * 100 : 0;
          return { ...p, noiActual, noiDiff, achievementRate, ...availability };
        })
        .sort((a, b) => b.noiActual - a.noiActual),
    [groupProperties, completedCount],
  );

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
      {items.map((p, idx) => (
        <Stack
          key={p.property_id}
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{
            py: 0.75,
            borderBottom:
              idx < items.length - 1 ? "1px solid #E0E0E0" : undefined,
          }}
        >
          <Box
            sx={{
              width: 240,
              flexShrink: 0,
              pl: 3,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
              <BuildingIcon />
            </Box>
            <Tooltip
              title={p.name.length > 15 ? p.name : ""}
              arrow
              placement="top"
            >
              <Typography
                onClick={() =>
                  navigate(
                    `/properties/${p.property_id}?tab=2&from=budget-analysis`,
                    { state: { propName: p.name } },
                  )
                }
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#3E3E3E",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationColor: "#3E3E3E",
                  textUnderlineOffset: "2px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "clip",
                  "&:hover": {
                    color: colors.primary.main,
                    textDecorationColor: colors.primary.main,
                  },
                }}
              >
                {p.name.length > 15 ? p.name.slice(0, 15) + "..." : p.name}
              </Typography>
            </Tooltip>
          </Box>
          {/* 右側: 親と同じ flex:1 + ml 構造 */}
          <Stack direction="row" sx={{ flex: 1 }} flexWrap="wrap">
            <Box sx={{ minWidth: 100, ml: 4 }}>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                NOI実績
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: financialValueColor(p.noiActual, p.noiActualAvailable),
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {p.noiActualAvailable ? fmtZero(p.noiActual) : "-"}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 100, ml: 1 }}>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                NOI差異
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: !p.noiDiffAvailable
                    ? "#666"
                    : p.noiDiff >= 0
                      ? "#2E7D32"
                      : colors.status.error,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {p.noiDiffAvailable ? formatSignedYen(p.noiDiff) : "-"}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 140, ml: 2 }}>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                達成率
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={Math.min(p.achievementRate, 100)}
                  sx={{ width: 64, height: 8, borderRadius: 1 }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#3E3E3E",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {p.achievementRate.toFixed(1)}%
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      ))}
    </Box>
  );
});

// ── グループサマリーカード ────────────────────────────

interface GroupSummary {
  id: string;
  name: string;
  agg: BudgetAnalysisMonthly[];
  incomeBudget: number;
  incomeActual: number;
  expenseBudget: number;
  expenseActual: number;
  noiBudget: number;
  noiActual: number;
  noiBudgetAvailable: boolean;
  noiActualAvailable: boolean;
  noiDiffAvailable: boolean;
  comparableNoiDiff: number;
  propertyCount: number;
  properties: BudgetAnalysisProperty[];
}

const GroupCard = React.memo(function GroupCard({
  group: g,
  color,
  onOpenDrawer,
  monthKeys,
  completedCount,
}: {
  group: GroupSummary;
  color: string;
  onOpenDrawer: (group: GroupSummary) => void;
  monthKeys: string[];
  completedCount: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const incomeRate =
    g.incomeBudget > 0 ? (g.incomeActual / g.incomeBudget) * 100 : 0;
  const noiDiff = g.comparableNoiDiff;
  // スパークライン: 詳細グラフと同じ、年度内全月の月次実績NOI推移。
  // 後続月の 0 を切ると「4月だけ実績あり」が右肩上がりに見えるため、
  // グループ集計済みの全月データをそのまま使う。
  const sparkData = useMemo(() => {
    return g.agg.map((m) => (m.income_actual || 0) - (m.expense_actual || 0));
  }, [g.agg]);

  return (
    <Box
      sx={{
        p: 1,
        backgroundColor: "#fff",
        borderRadius: "8px",
        border: "1px solid #E0E0E0",
      }}
    >
      <Stack direction="row" alignItems="center" flexWrap="wrap">
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            width: 26,
            height: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            cursor: "pointer",
            backgroundColor: expanded ? "#DBF8FF" : "transparent",
            color: expanded ? "#0E9DBC" : "#3e3e3e",
            transition: "all 0.2s",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "#DBF8FF",
              "& svg": { color: "#0E9DBC" },
            },
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              fontSize: 20,
              color: "inherit",
              transition: "transform 0.2s ease",
            }}
          />
        </Box>
        <Tooltip title={g.name.length > 12 ? g.name : ""} arrow placement="top">
          <Typography
            sx={{
              pl: 1,
              fontSize: "14px",
              fontWeight: 600,
              color: "#3E3E3E",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "clip",
            }}
          >
            {g.name.length > 12 ? g.name.slice(0, 12) + "..." : g.name}
          </Typography>
        </Tooltip>
        <CustomChip
          label={`${g.propertyCount}物件`}
          fontSize="12px"
          backgroundColor="#E8F5E9"
          color="#2E7D32"
          sx={{ mx: 3 }}
        />
        <MiniSparkline data={sparkData} color={color} />
        <Stack direction="row" sx={{ flex: 1 }} flexWrap="wrap">
          <Box sx={{ minWidth: 100, ml: 3 }}>
            <Typography sx={{ fontSize: "12px", color: "#666" }}>
              NOI実績
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: financialValueColor(g.noiActual, g.noiActualAvailable),
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {g.noiActualAvailable ? fmtZero(g.noiActual) : "-"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 100, ml: 1 }}>
            <Typography sx={{ fontSize: "12px", color: "#666" }}>
              NOI差異
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: !g.noiDiffAvailable
                  ? "#666"
                  : noiDiff >= 0
                    ? "#2E7D32"
                    : colors.status.error,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {g.noiDiffAvailable ? formatSignedYen(noiDiff) : "-"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 140, ml: 2 }}>
            <Typography sx={{ fontSize: "12px", color: "#666" }}>
              収入達成率
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <LinearProgress
                variant="determinate"
                value={Math.min(incomeRate, 100)}
                sx={{ width: 64, height: 8, borderRadius: 1 }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#3E3E3E",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {incomeRate.toFixed(1)}%
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onOpenDrawer(g)}
          startIcon={<BarChartIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 36,
            borderRadius: "10px",
            px: 2,
            backgroundColor: "#f7fbfd",
            borderColor: colors.primary.main,
            color: colors.primary.main,
            fontFamily: '"Noto Sans JP"',
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "170%",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#e9f6fa",
              borderColor: colors.primary.main,
            },
          }}
        >
          グラフ
        </Button>
      </Stack>
      {/* 展開時のみ PropertyBreakdown をマウント (Collapse は常にマウントするため) */}
      <Collapse in={expanded} timeout={250} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          {expanded && (
            <PropertyBreakdown
              groupProperties={g.properties}
              monthKeys={monthKeys}
              completedCount={completedCount}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

// ── 集計ロジックは ./budgetAnalysisAgg に集約 (unit テスト対象) ──

// ── Main Page ─────────────────────────────────────────

const BudgetAnalysisPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { data, loading } = useSelector(
    (s: RootState) => (s as any).budgetAnalysis,
  );

  // 会計年度開始月 = 決算月 + 1 (12月決算なら 1月)
  const fiscalClosingMonth: number =
    useSelector(
      (s: RootState) =>
        (s.clients?.detailed?.data as any)?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;

  const defaultYear = useMemo(() => {
    const now = new Date();
    const fy =
      now.getMonth() + 1 >= fiscalYearStartMonth
        ? now.getFullYear()
        : now.getFullYear() - 1;
    return String(fy);
  }, [fiscalYearStartMonth]);

  const groupBy = "group" as GroupBy;
  const metric = "noi" as Metric;
  const [fiscalYear, setFiscalYear] = useState<string>(defaultYear);
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );
  const [summaryLoading, setSummaryLoading] = useState(loading);
  const effectiveSummaryLoading = loading || summaryLoading;
  // 年度ラベル: 今年度ならその旨を明記、それ以外は「YYYY年度」
  const fiscalYearLabel = useMemo(() => {
    const y = Number(fiscalYear);
    return String(y) === defaultYear ? `今年度（${y}年度）` : `${y}年度`;
  }, [fiscalYear, defaultYear]);
  // fiscal_closing_month が非同期で取れた直後の 1 回だけ defaultYear に追従
  const fiscalYearInitSyncedRef = useRef(false);
  useEffect(() => {
    if (!fiscalYearInitSyncedRef.current) {
      setFiscalYear(defaultYear);
      fiscalYearInitSyncedRef.current = true;
    }
  }, [defaultYear]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(
    new Set(),
  );
  const [visibleGroupIds, setVisibleGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [cardsExpanded, setCardsExpanded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerGroup, setDrawerGroup] = useState<GroupSummary | null>(null);
  const [drawerPropertyId, setDrawerPropertyId] = useState<string | null>(null);
  /** CustomDrawer は drawerGroup 初回マウント時に open=true だとスライドがスキップされるため、1フレーム閉じた状態でマウントしてから開く */
  const drawerShellMountedRef = useRef(false);

  // 安定参照: GroupCard の React.memo を効かせるため useCallback 化
  const handleOpenDrawer = useCallback((group: GroupSummary) => {
    setDrawerGroup(group);
    setDrawerPropertyId(group.properties[0]?.property_id || null);
    if (!drawerShellMountedRef.current) {
      setDrawerOpen(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDrawerOpen(true);
          drawerShellMountedRef.current = true;
        });
      });
    } else {
      setDrawerOpen(true);
    }
  }, []);
  const [cardSearchQuery, setCardSearchQuery] = useState("");
  const [chartMetric, setChartMetric] = useState<Metric>("income");

  // フィルタ用の form (CustomSingleSelect 用)
  const {
    register,
    reset: resetForm,
    getValues,
    watch,
    formState: { errors },
  } = useForm<{
    fiscalYear: string;
    groupBy: GroupBy;
    metric: Metric;
    groupSearch: string;
  }>({
    defaultValues: {
      fiscalYear: defaultYear,
      groupBy: "group",
      metric: "noi",
      groupSearch: "",
    },
  });

  const fetchDetail = useCallback(async () => {
    setSummaryLoading(true);
    setInitialLoadError(null);
    try {
      await dispatch(fetchBudgetAnalysis({ fiscal_year: fiscalYear })).unwrap();
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
    } finally {
      setSummaryLoading(false);
    }
  }, [dispatch, fiscalYear]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // 年度切替時はドロワーを即時クローズして、古い drawerGroup と新しい monthKeys
  // の不整合 (xAxis vs series 長さ乖離) でチャートがクラッシュするのを防ぐ。
  useEffect(() => {
    setDrawerOpen(false);
    setDrawerGroup(null);
    setDrawerPropertyId(null);
    drawerShellMountedRef.current = false;
  }, [fiscalYear]);

  // data?.x ?? [] は毎レンダー新しい空配列を作り、依存する groups → setVisibleGroupIds
  // (useEffect) の無限ループを招くため、参照を安定させる。
  const properties: BudgetAnalysisProperty[] = useMemo(
    () => data?.properties ?? [],
    [data],
  );
  const monthKeys: string[] = useMemo(() => data?.month_keys ?? [], [data]);

  // 当日の暦年月 (セッション中は不変扱い)
  const { currentYear, currentMonth } = useMemo(() => {
    const now = new Date();
    return { currentYear: now.getFullYear(), currentMonth: now.getMonth() + 1 };
  }, []);

  // 表示中の会計年度における
  //  - completedCount: 「前月まで」= 当月より前で確定済みの先頭月数 (時系列の prefix)
  //  - currentMonthIndex: 「今月」が年度内にある場合のその index、無ければ -1
  // 過去年度は全月確定 (=12) / 未来年度は 0、今月の縦線は今年度表示時のみ出る。
  const { currentMonthIndex } = useMemo(
    () =>
      computeMonthProgress(
        monthKeys,
        fiscalYearStartMonth,
        Number(fiscalYear),
        currentYear,
        currentMonth,
      ),
    [monthKeys, fiscalYearStartMonth, fiscalYear, currentYear, currentMonth],
  );
  // 「前月まで」(completedCount) は撤廃。集計・グラフ・スパークラインは全月
  // (=データのある月) を対象にする。実績は入力済みの月だけ表示される。
  const completedCount = monthKeys.length;

  // ── グルーピング ──────────────────────────────────
  const groups = useMemo(() => {
    if (groupBy === "group") {
      const byName = new Map<string, BudgetAnalysisProperty[]>();
      for (const p of properties) {
        const key = p.group_name || "グループなし";
        const arr = byName.get(key) || [];
        arr.push(p);
        byName.set(key, arr);
      }
      return (
        Array.from(byName.entries())
          .map(([name, props]) => ({
            id: `g-${name}`,
            name,
            properties: props,
          }))
          // 「グループなし」は常に最下部に表示する (他グループは元の順序を維持)
          .sort(
            (a, b) =>
              (a.name === "グループなし" ? 1 : 0) -
              (b.name === "グループなし" ? 1 : 0),
          )
      );
    }
    // custom
    const selected = properties.filter((p) =>
      selectedPropertyIds.has(p.property_id),
    );
    return selected.length > 0
      ? [{ id: "custom", name: "選択物件", properties: selected }]
      : [];
  }, [groupBy, properties, selectedPropertyIds]);

  // 初期表示で全グループを可視化。ID集合が同じなら同一参照を返してループを防ぐ。
  useEffect(() => {
    setVisibleGroupIds((prev) => {
      const next = new Set(groups.map((g) => g.id));
      if (prev.size === next.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, [groups]);

  const groupSummaries = useMemo(() => {
    return groups.map((g) => {
      // agg は月次の生集計 (スパークライン/月次グラフ用)。
      // カード等のスカラー合計は全月そのまま合算する (ゲートなし)。
      const agg = aggregateMonthly(g.properties, monthKeys);
      const t = aggregateTotals(g.properties, completedCount);
      const availability = aggregateNoiAvailability(
        g.properties,
        completedCount,
      );
      const comparableNoi = aggregateComparableNoi(
        g.properties,
        completedCount,
      );
      return {
        ...g,
        agg,
        incomeBudget: t.incomeBudget,
        incomeActual: t.incomeActual,
        expenseBudget: t.expenseBudget,
        expenseActual: t.expenseActual,
        noiBudget: t.noiBudget,
        noiActual: t.noiActual,
        ...availability,
        noiDiffAvailable: comparableNoi.available,
        comparableNoiDiff: comparableNoi.diff,
        propertyCount: g.properties.length,
      };
    });
  }, [groups, monthKeys, completedCount]);

  // ── カード検索 + 折りたたみ ──
  const filteredSummaries = useMemo(() => {
    if (!cardSearchQuery.trim()) return groupSummaries;
    const q = cardSearchQuery.toLowerCase();
    return groupSummaries.filter((g) => g.name.toLowerCase().includes(q));
  }, [groupSummaries, cardSearchQuery]);

  const shouldCollapse = filteredSummaries.length > COLLAPSED_VISIBLE;
  const visibleSummaries =
    shouldCollapse && !cardsExpanded
      ? filteredSummaries.slice(0, COLLAPSED_VISIBLE)
      : filteredSummaries;
  const hiddenCount = filteredSummaries.length - COLLAPSED_VISIBLE;

  // groupBy 切り替え時にカード状態をリセット
  useEffect(() => {
    setCardsExpanded(false);
    setCardSearchQuery("");
  }, [groupBy]);

  // ── ポートフォリオ全体の月次集計 (月次グラフ用の生集計)
  // 折れ線グラフ (portfolioChartData) 用。累計サマリー (portfolioTotals) は
  // スカラー合計は aggregateTotals で別途集計するため、ここは生の月次合計を保持。
  const portfolioAgg = useMemo(
    () => aggregateMonthly(properties, monthKeys),
    [properties, monthKeys],
  );

  // ── ポートフォリオ全体エリアチャート用 ──
  const portfolioChartData = useMemo(
    () => buildPortfolioChartData(portfolioAgg, chartMetric),
    [portfolioAgg, chartMetric],
  );

  // ── カスタム時の物件別積み上げ ──
  const stackedBarSeries = useMemo(() => {
    if (groupBy !== "custom" || selectedPropertyIds.size === 0) return [];
    const selected = properties.filter((p) =>
      selectedPropertyIds.has(p.property_id),
    );
    return selected.map((p, idx) => {
      const baseColor = SERIES_COLORS[idx % SERIES_COLORS.length];
      return {
        id: p.property_id,
        label: p.name,
        data: p.monthly.map((m) => pickMetric(m, metric, "actual")),
        color: baseColor,
        stack: "total",
      };
    });
  }, [groupBy, properties, selectedPropertyIds, metric]);

  // ── ポートフォリオ全体集計 ──
  // 全月そのまま合算 (ゲートなし)。累計サマリーの基準。
  const portfolioTotals = useMemo(
    () => aggregateTotals(properties, completedCount),
    [properties, completedCount],
  );

  const metricLabel =
    metric === "noi" ? "NOI" : metric === "income" ? "収入" : "支出";

  // ── 比較ラインチャート用 series ──
  const lineSeries = useMemo(() => {
    const visible = groupSummaries.filter((g) => visibleGroupIds.has(g.id));
    const series: Array<{
      id: string;
      data: number[];
      label: string;
      color: string;
      dash?: boolean;
    }> = [];
    visible.forEach((g) => {
      series.push({
        id: `${g.id}-budget`,
        data: g.agg.map((m) => pickMetric(m, metric, "budget")),
        label: `${g.name} 予算`,
        color: CHART_BUDGET_COLOR,
        dash: true,
      });
      series.push({
        id: `${g.id}-actual`,
        data: g.agg.map((m) => pickMetric(m, metric, "actual")),
        label: `${g.name} 実績`,
        color: CHART_ACTUAL_COLOR,
      });
    });
    return series;
  }, [groupSummaries, visibleGroupIds, metric]);

  // ── YTD 横棒チャート用 ──
  const barData = useMemo(() => {
    const visible = groupSummaries.filter((g) => visibleGroupIds.has(g.id));
    return visible.map((g) => {
      const budget =
        metric === "noi"
          ? g.noiBudget
          : metric === "income"
            ? g.incomeBudget
            : g.expenseBudget;
      const actual =
        metric === "noi"
          ? g.noiActual
          : metric === "income"
            ? g.incomeActual
            : g.expenseActual;
      return { name: g.name, budget, actual };
    });
  }, [groupSummaries, visibleGroupIds, metric]);

  const toggleGroup = (id: string) => {
    setVisibleGroupIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleProperty = (id: string) => {
    setSelectedPropertyIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const monthLabels = useMemo(
    () => monthKeys.map((mk) => MONTH_LABELS[mk] || mk),
    [monthKeys],
  );

  /* テーブル削除済み — tableRows / tableColumns 未使用 */
  /* const tableRows = useMemo(
    () =>
      groupSummaries.map((g) => {
        const incomeRate =
          g.incomeBudget > 0 ? (g.incomeActual / g.incomeBudget) * 100 : 0;
        const noiDiff = g.noiActual - g.noiBudget;
        return {
          id: g.id,
          name: g.name,
          propertyCount: g.propertyCount,
          incomeBudget: g.incomeBudget,
          incomeActual: g.incomeActual,
          incomeRate,
          expenseBudget: g.expenseBudget,
          expenseActual: g.expenseActual,
          noiBudget: g.noiBudget,
          noiActual: g.noiActual,
          noiDiff,
        };
      }),
    [groupSummaries],
  );

  const numCellSx = {
    fontSize: "14px",
    fontVariantNumeric: "tabular-nums" as const,
    color: "#3E3E3E",
  };

  const tableColumns: CustomColumnDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "グループ名",
        flex: 1.6,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: "#3E3E3E" }}
          >
            {String(value)}
          </Typography>
        ),
      },
      {
        field: "propertyCount",
        headerName: "物件数",
        flex: 0.6,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{ fontSize: "14px", color: "#666", textAlign: "center" }}
          >
            {value}
          </Typography>
        ),
      },
      {
        field: "incomeBudget",
        headerName: "収入予算",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{
              ...numCellSx,
              color:
                Number(value || 0) < 0 ? colors.status.error : "#666",
            }}
          >
            {fmt(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "incomeActual",
        headerName: "収入実績",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{
              ...numCellSx,
              color:
                Number(value || 0) < 0 ? colors.status.error : undefined,
            }}
          >
            {fmt(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "incomeRate",
        headerName: "達成率",
        flex: 0.7,
        sortable: true,
        renderCell: ({ value }) => {
          const rate = Number(value || 0);
          return (
            <Typography
              sx={{
                ...numCellSx,
                color: rate >= 100 ? "#2E7D32" : "#3E3E3E",
              }}
            >
              {rate.toFixed(1)}%
            </Typography>
          );
        },
      },
      {
        field: "expenseBudget",
        headerName: "支出予算",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{
              ...numCellSx,
              color:
                Number(value || 0) < 0 ? colors.status.error : "#666",
            }}
          >
            {fmt(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "expenseActual",
        headerName: "支出実績",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{
              ...numCellSx,
              color:
                Number(value || 0) < 0 ? colors.status.error : undefined,
            }}
          >
            {fmt(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "noiBudget",
        headerName: "NOI予算",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{
              ...numCellSx,
              color:
                Number(value || 0) < 0 ? colors.status.error : "#666",
            }}
          >
            {fmt(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "noiActual",
        headerName: "NOI実績",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography
            sx={{
              ...numCellSx,
              fontWeight: 600,
              color: financialValueColor(Number(value || 0)),
            }}
          >
            {fmt(Number(value || 0))}
          </Typography>
        ),
      },
      {
        field: "noiDiff",
        headerName: "NOI差異",
        flex: 0.9,
        sortable: true,
        renderCell: ({ value }) => {
          const v = Number(value || 0);
          return (
            <Typography
              sx={{
                ...numCellSx,
                fontWeight: 600,
                color: v >= 0 ? "#2E7D32" : colors.status.error,
              }}
            >
              {v >= 0 ? "+" : ""}
              {fmt(v)}
            </Typography>
          );
        },
      },
    ],
    [],
  ); */

  const hasBudgetAnalysisCache = properties.length > 0;
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasBudgetAnalysisCache,
    dedupeKey: `budget-analysis:${fiscalYear}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasBudgetAnalysisCache)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  return (
    <Box sx={{ my: 3, overflow: "hidden" }}>
      <CustomSectionTitle title="予実分析" size="large" />

      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "calc(100vh - 220px)",
            }}
          >
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        )}

        {!loading && properties.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              minHeight: "160px",
            }}
          >
            <BarChartIcon
              sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: colors.text.secondary,
                textAlign: "center",
              }}
            >
              表示できる予実データがありません
            </Typography>
          </Box>
        )}

        {!loading && properties.length > 0 && (
          <>
            {/* Custom Property Selector — 一旦無効化（ドロワーで詳細確認できるため） */}
            {false && groupBy === "custom" && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box sx={{ width: "auto" }}>
                    <CustomSectionTitle title="物件選択" addBorder={false} />
                  </Box>
                  <CustomChip
                    label={`${selectedPropertyIds.size}/${properties.length}件選択中`}
                    fontSize="12px"
                    backgroundColor="#E8F5E9"
                    color="#2E7D32"
                  />
                  <Box sx={{ flex: 1 }} />
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedPropertyIds(
                          new Set(properties.map((p) => p.property_id)),
                        )
                      }
                      sx={{
                        height: 36,
                        minWidth: 74,
                        borderRadius: "10px",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        backgroundColor: colors.primary.main,
                        color: "#fff",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: colors.primary.main,
                          boxShadow: "none",
                        },
                      }}
                    >
                      全選択
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedPropertyIds(new Set())}
                      sx={{
                        height: 36,
                        minWidth: 74,
                        borderRadius: "10px",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        backgroundColor: "#fff",
                        color: colors.text.secondary,
                        border: `1px solid ${colors.border.medium}`,
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#fff",
                          boxShadow: "none",
                        },
                      }}
                    >
                      全解除
                    </Button>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr",
                    },
                    gap: 1,
                  }}
                >
                  {properties.map((p) => {
                    const checked = selectedPropertyIds.has(p.property_id);
                    return (
                      <Box
                        key={p.property_id}
                        onClick={() => toggleProperty(p.property_id)}
                        sx={{
                          p: 1.5,
                          border: "1px solid #E0E0E0",
                          borderRadius: "8px",
                          cursor: "pointer",
                          backgroundColor: checked ? "#E6F7FB" : "#fff",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          "&:hover": {
                            backgroundColor: "#E6F7FB",
                          },
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          size="small"
                          sx={{ p: 0, mt: 0.25 }}
                        />
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            mt: 0.25,
                          }}
                        >
                          <BuildingIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#3E3E3E",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              {p.name}
                            </Typography>
                            {p.group_name && (
                              <CustomChip
                                label={p.group_name}
                                fontSize="12px"
                                backgroundColor="#F5F5F5"
                                color="#666"
                              />
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Portfolio Overview: KPI 左 + AreaChart 右 */}
            {groupBy === "group" && (
              <Box sx={{ mt: 1 }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box sx={{ width: "auto" }}>
                    <CustomSectionTitle
                      title="累計サマリー"
                      addBorder={false}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0}
                    sx={{ position: "relative", top: "-1.5px" }}
                  >
                    <Box
                      onClick={() => {
                        setSummaryLoading(true);
                        setInitialLoadError(null);
                        setFiscalYear(String(Number(fiscalYear) - 1));
                      }}
                      sx={{
                        width: 26,
                        height: 26,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        cursor: "pointer",
                        color: "#3e3e3e",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#DBF8FF",
                          "& svg": { color: "#0E9DBC" },
                        },
                      }}
                    >
                      <ChevronLeftIcon
                        sx={{ fontSize: 20, color: "inherit" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#3E3E3E",
                        px: 0.5,
                      }}
                    >
                      {fiscalYear}
                    </Typography>
                    <Box
                      onClick={() => {
                        setSummaryLoading(true);
                        setInitialLoadError(null);
                        setFiscalYear(String(Number(fiscalYear) + 1));
                      }}
                      sx={{
                        width: 26,
                        height: 26,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        cursor: "pointer",
                        color: "#3e3e3e",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#DBF8FF",
                          "& svg": { color: "#0E9DBC" },
                        },
                      }}
                    >
                      <ChevronRightIcon
                        sx={{ fontSize: 20, color: "inherit" }}
                      />
                    </Box>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    px: 2,
                    pt: 2,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
                    {/* Left: KPI (41%) */}
                    <Box sx={{ width: { lg: "41%" }, flexShrink: 0 }}>
                      <CustomSummaryCard
                        label="NOI実績"
                        labelSize="12px"
                        valueSize="20px"
                        value={fmtZero(portfolioTotals.noiActual)}
                        valueColor={financialValueColor(
                          portfolioTotals.noiActual,
                        )}
                        loading={effectiveSummaryLoading}
                        loadingDelayMs={150}
                        // sub={
                        //   noiNoCompareData ? (
                        //     <Typography
                        //       sx={{ fontSize: "12px", color: "#666" }}
                        //     >
                        //       -
                        //     </Typography>
                        //   ) : (
                        //     <Stack
                        //       direction="row"
                        //       spacing={0.5}
                        //       alignItems="center"
                        //     >
                        //       <Typography
                        //         sx={{ fontSize: "12px", color: "#666" }}
                        //       >
                        //         {fmt(portfolioTotals.noiBudget)}
                        //       </Typography>
                        //       <Typography
                        //         sx={{
                        //           fontSize: "12px",
                        //           fontWeight: 500,
                        //           color: noiBeatBudget ? "#2E7D32" : "#e53935",
                        //         }}
                        //       >
                        //         {noiBeatBudget ? "+" : ""}
                        //         {noiDiffPortfolio === 0
                        //           ? "0"
                        //           : fmtShort(noiDiffPortfolio)}
                        //       </Typography>
                        //     </Stack>
                        //   )
                        // }
                        sx={{ backgroundColor: "#F9FCFE" }}
                      />

                      <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                        <CustomSummaryCard
                          label="収入実績"
                          labelSize="12px"
                          valueSize="20px"
                          value={fmtZero(portfolioTotals.incomeActual)}
                          valueColor={
                            portfolioTotals.incomeActual < 0
                              ? colors.status.error
                              : undefined
                          }
                          loading={effectiveSummaryLoading}
                          loadingDelayMs={150}
                          // sub={
                          //   <Typography sx={{ fontSize: "12px", color: "#999" }}>
                          //     {`達成率 ${portfolioTotals.incomeBudget > 0 ? ((portfolioTotals.incomeActual / portfolioTotals.incomeBudget) * 100).toFixed(1) : 0}%`}
                          //   </Typography>
                          // }
                          sx={{ flex: 1 }}
                        />
                        <CustomSummaryCard
                          label="支出実績"
                          labelSize="12px"
                          valueSize="20px"
                          value={fmtZero(portfolioTotals.expenseActual)}
                          valueColor={
                            portfolioTotals.expenseActual < 0
                              ? colors.status.error
                              : undefined
                          }
                          loading={effectiveSummaryLoading}
                          loadingDelayMs={150}
                          // sub={
                          //   <Typography sx={{ fontSize: "12px", color: "#999" }}>
                          //     {`執行率 ${portfolioTotals.expenseBudget > 0 ? ((portfolioTotals.expenseActual / portfolioTotals.expenseBudget) * 100).toFixed(1) : 0}%`}
                          //   </Typography>
                          // }
                          sx={{ flex: 1 }}
                        />
                      </Stack>

                      <CustomSummaryCard
                        label="物件数 / グループ数"
                        labelSize="12px"
                        valueSize="16px"
                        value={`${properties.length}物件 / ${groups.length}グループ`}
                        loading={effectiveSummaryLoading}
                        loadingDelayMs={150}
                        sx={{ mt: 1 }}
                      />
                    </Box>

                    {/* Right: AreaChart */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#3E3E3E",
                            }}
                          >
                            {chartMetric === "noi"
                              ? "NOI"
                              : chartMetric === "income"
                                ? "収入"
                                : "支出"}{" "}
                            全体推移
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            全物件合計の予算 vs 実績
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {(["income", "expense", "noi"] as Metric[]).map(
                            (m) => (
                              <Button
                                key={m}
                                size="small"
                                variant={
                                  chartMetric === m ? "contained" : "text"
                                }
                                disableElevation
                                onClick={() => setChartMetric(m)}
                                sx={{
                                  fontSize: "12px",
                                  textTransform: "none",
                                  borderRadius: "20px",
                                  minWidth: 0,
                                  px: 1.5,
                                  py: 0.25,
                                  boxShadow: "none",
                                  ...(chartMetric === m
                                    ? {
                                        backgroundColor: colors.primary.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors.primary.main,
                                          boxShadow: "none",
                                        },
                                      }
                                    : { color: "#666" }),
                                }}
                              >
                                {m === "noi"
                                  ? "NOI"
                                  : m === "income"
                                    ? "収入"
                                    : "支出"}
                              </Button>
                            ),
                          )}
                        </Stack>
                        <ChartLegend />
                      </Stack>
                      {monthLabels.length > 0 && (
                        <Box sx={{ flex: 1 }}>
                          <LineChart
                            xAxis={[{ data: monthLabels, scaleType: "point" }]}
                            yAxis={[
                              {
                                valueFormatter: (v: number | null) =>
                                  fmtChart(v),
                              },
                            ]}
                            series={[
                              {
                                id: "portfolio-budget",
                                data: portfolioChartData.budget,
                                label: "予算",
                                color: CHART_BUDGET_COLOR,
                                area: true,
                                showMark: false,
                                // ツールチップは全桁 (¥+カンマ)。軸の fmtChart(万) とは別。
                                valueFormatter: (v: number | null) =>
                                  fmtZero(v ?? 0),
                              },
                              {
                                id: "portfolio-actual",
                                data: portfolioChartData.actual,
                                label: "実績",
                                color: CHART_ACTUAL_COLOR,
                                area: true,
                                showMark: true,
                                // 予実分析は集計結果なので 0/未入力も 0 として全桁表示 (¥0)。
                                valueFormatter: (v: number | null) =>
                                  fmtZero(v ?? 0),
                              },
                            ]}
                            height={250}
                            margin={{ top: 10 }}
                            slots={{ axisContent: ChartTooltipContent }}
                            slotProps={{
                              legend: { hidden: true },
                            }}
                            sx={{
                              "& .MuiAreaElement-series-portfolio-actual": {
                                fill: "url(#actualGradient)",
                              },
                              "& .MuiAreaElement-series-portfolio-budget": {
                                fill: "url(#budgetGradient)",
                              },
                              "& .MuiLineElement-series-portfolio-budget": {
                                strokeDasharray: "6 4",
                              },
                              "& .MuiChartsAxisHighlight-root": {
                                stroke: CHART_HIGHLIGHT_COLOR,
                              },
                            }}
                          >
                            {currentMonthIndex >= 0 && (
                              <ChartsReferenceLine
                                x={monthLabels[currentMonthIndex]}
                                label="今月"
                                labelAlign="start"
                                lineStyle={{
                                  stroke: CHART_TODAY_COLOR,
                                  strokeDasharray: "4 3",
                                  strokeWidth: 1.5,
                                }}
                                labelStyle={{
                                  fontSize: 11,
                                  fill: CHART_TODAY_COLOR,
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            <defs>
                              <linearGradient
                                id="actualGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="#0B9DBD"
                                  stopOpacity={0.3}
                                />
                                <stop
                                  offset="100%"
                                  stopColor="#0B9DBD"
                                  stopOpacity={0.02}
                                />
                              </linearGradient>
                              <linearGradient
                                id="budgetGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="#DBF8FF"
                                  stopOpacity={0.4}
                                />
                                <stop
                                  offset="100%"
                                  stopColor="#DBF8FF"
                                  stopOpacity={0.02}
                                />
                              </linearGradient>
                            </defs>
                          </LineChart>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            )}

            {/* Group Summary Cards */}
            {groupSummaries.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" sx={{ my: 1 }}>
                  <CustomSectionTitle
                    title={
                      groupBy === "group" ? "グループサマリー" : "物件サマリー"
                    }
                    addBorder={false}
                    sx={{ width: "auto" }}
                  />
                </Stack>
                {groupBy === "group" && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ sm: "center" }}
                    sx={{ mb: 1 }}
                  >
                    <Box sx={{ minWidth: 280, maxWidth: 560, flex: 1 }}>
                      <CustomTextInput
                        label=""
                        name="groupSearch"
                        placeholder="グループ名で絞り込み"
                        register={register}
                        errors={errors}
                        inputWidth="100%"
                        inputWidthSp="100%"
                        showSearchIconStart
                        search
                        showClearButton={
                          resolveSearchControls({
                            keyword: watch("groupSearch"),
                            hasFilterControls: false,
                          }).showKeywordClear
                        }
                        onClearClick={() => {
                          resetForm({ ...getValues(), groupSearch: "" });
                          setCardSearchQuery("");
                        }}
                        clearButtonAriaLabel="キーワードをクリア"
                        sx={{ mb: "0px!important" }}
                      />
                    </Box>
                    <Stack direction="row" spacing={1.25}>
                      <CustomButton
                        label="検索"
                        buttonCategory="search"
                        onClick={() => {
                          setCardSearchQuery(getValues("groupSearch") || "");
                          setCardsExpanded(true);
                        }}
                      />
                    </Stack>
                  </Stack>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {visibleSummaries.map((g) => {
                    return (
                      <GroupCard
                        key={g.id}
                        group={g}
                        color={SPARKLINE_COLOR}
                        monthKeys={monthKeys}
                        completedCount={completedCount}
                        onOpenDrawer={handleOpenDrawer}
                      />
                    );
                  })}
                </Box>

                {shouldCollapse && !cardSearchQuery.trim() && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 1,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => setCardsExpanded(!cardsExpanded)}
                      startIcon={
                        cardsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                      }
                      sx={{ fontSize: "12px", color: "#0B9DBD", width: "100%" }}
                    >
                      {cardsExpanded
                        ? "折りたたむ"
                        : `他${hiddenCount}件を表示`}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Group visibility chips — 一旦無効化（ドロワーで詳細確認できるため） */}
            {false && groupSummaries.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box sx={{ width: "auto" }}>
                    <CustomSectionTitle
                      title={`${metricLabel} 予実推移`}
                      addBorder={false}
                    />
                  </Box>
                  <CustomChip
                    label={`${visibleGroupIds.size}/${groups.length}件表示中`}
                    fontSize="12px"
                    backgroundColor="#E8F5E9"
                    color="#2E7D32"
                  />
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {groupSummaries.map((g) => {
                    const isOn = visibleGroupIds.has(g.id);
                    return (
                      <CustomChip
                        key={g.id}
                        label={g.name}
                        onClick={() => toggleGroup(g.id)}
                        backgroundColor={isOn ? colors.primary.main : "#fff"}
                        color={isOn ? "#fff" : "#666"}
                        borderColor={isOn ? colors.primary.main : "#E0E0E0"}
                        fontWeight={500}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Charts — 一旦無効化（ドロワーで詳細確認できるため） */}
            {false &&
              visibleGroupIds.size > 0 &&
              lineSeries.length > 0 &&
              monthLabels.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#3E3E3E",
                          }}
                        >
                          {metricLabel} 予実推移
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                          選択物件合計の月次推移
                        </Typography>
                      </Box>
                      <ChartLegend />
                    </Stack>
                    <Box sx={{ height: 320 }}>
                      <LineChart
                        xAxis={[{ data: monthLabels, scaleType: "point" }]}
                        yAxis={[
                          {
                            valueFormatter: (v: number | null) => fmtChart(v),
                          },
                        ]}
                        series={lineSeries.map((s) => ({
                          id: s.id,
                          data: s.data,
                          label: s.label,
                          color: s.color,
                          showMark: false,
                        }))}
                        height={320}
                        margin={{ top: 20 }}
                        slots={{ axisContent: ChartTooltipContent }}
                        slotProps={{
                          legend: { hidden: true },
                        }}
                        sx={lineSeries.reduce(
                          (acc, s) => {
                            if (s.dash) {
                              acc[`& .MuiLineElement-series-${s.id}`] = {
                                strokeDasharray: "4 4",
                              };
                            }
                            return acc;
                          },
                          {} as Record<string, any>,
                        )}
                      />
                    </Box>
                  </Box>

                  {/* YTD bar chart (group mode) */}
                  {groupBy === "group" && barData.length > 0 && (
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#3E3E3E",
                            }}
                          >
                            {metricLabel} 累計比較
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            選択グループの{fiscalYearLabel}累計 予算 vs 実績
                          </Typography>
                        </Box>
                        <ChartLegend />
                      </Stack>
                      <Box sx={{ height: 320 }}>
                        <BarChart
                          yAxis={[
                            {
                              data: barData.map((d) => d.name),
                              scaleType: "band",
                            },
                          ]}
                          xAxis={[
                            {
                              valueFormatter: (v: number | null) => fmtChart(v),
                            },
                          ]}
                          series={[
                            {
                              data: barData.map((d) => d.budget),
                              label: "予算",
                              color: CHART_BUDGET_COLOR,
                            },
                            {
                              data: barData.map((d) => d.actual),
                              label: "実績",
                              color: CHART_ACTUAL_COLOR,
                            },
                          ]}
                          layout="horizontal"
                          height={320}
                          margin={{ left: 120, right: 20, top: 10, bottom: 30 }}
                          slots={{ axisContent: ChartTooltipContent }}
                          slotProps={{
                            legend: { hidden: true },
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Stacked bar chart (custom mode) */}
                  {groupBy === "custom" &&
                    stackedBarSeries.length > 0 &&
                    monthLabels.length > 0 &&
                    stackedBarSeries[0].data.length === monthLabels.length && (
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#3E3E3E",
                          }}
                        >
                          {metricLabel} 物件別積み上げ
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", color: "#666", mb: 1 }}
                        >
                          選択物件ごとの構成比を月別に確認
                        </Typography>
                        {/* 積み上げは物件ごとの色なので凡例はチャート内蔵のまま */}
                        <Box sx={{ height: 320 }}>
                          <BarChart
                            xAxis={[{ data: monthLabels, scaleType: "band" }]}
                            yAxis={[
                              {
                                valueFormatter: (v: number | null) =>
                                  fmtChart(v),
                              },
                            ]}
                            series={stackedBarSeries.map((s) => ({
                              data: s.data,
                              label: s.label,
                              color: s.color,
                              stack: s.stack,
                            }))}
                            height={320}
                            slots={{ axisContent: ChartTooltipContent }}
                            slotProps={{
                              legend: { hidden: true },
                            }}
                            sx={{
                              "& .MuiChartsTooltip-root": { fontSize: 12 },
                              "& .MuiChartsTooltip-cell": {
                                fontSize: 12,
                                py: 0.25,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                </Box>
              )}
          </>
        )}
      </Box>

      {/* グラフドロワー（ページレベルで1つだけ）: drawerGroup が残っている間は mount したままにし、
          drawerOpen=false で閉じる時も CustomDrawer 側のスライドアウトアニメーションを走らせる。
          drawerGroup が null の間は重い計算をスキップ。 */}
      {drawerGroup &&
        (() => {
          const dg = drawerGroup;
          const noiDiff = dg.comparableNoiDiff;
          const selectedProp = drawerPropertyId
            ? dg.properties.find((p) => p.property_id === drawerPropertyId) ||
              dg.properties[0]
            : dg.properties[0];
          // ポートフォリオ折れ線と同じ扱い: 集計結果なので 0/未入力も実データ(0)として
          // 全月に点を出す (予算も実績も 0 の月でも丸を出したい要件のため null にしない)。
          const propActualNoi: (number | null)[] =
            selectedProp?.monthly.map(
              (m) => (m.income_actual || 0) - (m.expense_actual || 0),
            ) ?? [];
          // 予算は年間通して表示
          const propBudgetNoi =
            selectedProp?.monthly.map(
              (m) => (m.income_budget || 0) - (m.expense_budget || 0),
            ) ?? [];
          // チップ/棒の実績・予算合計は全月そのまま合算 (ゲートなし)
          const propTotals = selectedProp
            ? aggregateTotals([selectedProp], completedCount)
            : { noiActual: 0, noiBudget: 0 };
          const propNoiActual = propTotals.noiActual;
          const propNoiBudget = propTotals.noiBudget;
          const propBarData = dg.properties.map((p) => {
            const t = aggregateTotals([p], completedCount);
            return {
              name: p.name.length > 12 ? p.name.slice(0, 12) + "…" : p.name,
              budget: t.noiBudget,
              actual: t.noiActual,
            };
          });
          const drawerMonthLabels = monthKeys.map(
            (mk) => MONTH_LABELS[mk] || mk,
          );

          return (
            <PreviewDrawer
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              onExpand={() => {
                setDrawerOpen(false);
                navigate(
                  `/budget-analysis/group/${encodeURIComponent(dg.name)}?fiscal_year=${fiscalYear}`,
                );
              }}
              width="70%"
            >
              {effectiveSummaryLoading ? (
                <Box
                  sx={{
                    minHeight: "calc(100vh - 180px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CustomLoading
                    loading={true}
                    fullScreen={false}
                    size="medium"
                  />
                </Box>
              ) : (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: "#E6F7FB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <BarChartIcon
                        sx={{ fontSize: 18, color: colors.primary.main }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "#3E3E3E",
                        }}
                      >
                        {dg.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomChip
                          label={`${dg.propertyCount}物件`}
                          fontSize="12px"
                          backgroundColor="#E8F5E9"
                          color="#2E7D32"
                        />
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                          物件別NOI予実分析
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                  </Stack>

                  <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
                    <CustomSummaryCard
                      label="NOI実績合計"
                      labelSize="12px"
                      valueSize="16px"
                      value={
                        dg.noiActualAvailable ? fmtZero(dg.noiActual) : "-"
                      }
                      valueColor={financialValueColor(
                        dg.noiActual,
                        dg.noiActualAvailable,
                      )}
                      loading={effectiveSummaryLoading}
                      loadingDelayMs={150}
                      sx={{ flex: 1, p: 1.5 }}
                    />
                    <CustomSummaryCard
                      label="NOI予算合計"
                      labelSize="12px"
                      valueSize="16px"
                      value={
                        dg.noiBudgetAvailable ? fmtZero(dg.noiBudget) : "-"
                      }
                      valueColor={financialValueColor(
                        dg.noiBudget,
                        dg.noiBudgetAvailable,
                      )}
                      loading={effectiveSummaryLoading}
                      loadingDelayMs={150}
                      sx={{ flex: 1, p: 1.5 }}
                    />
                    <CustomSummaryCard
                      label="差異"
                      labelSize="12px"
                      valueSize="16px"
                      value={
                        dg.noiDiffAvailable ? formatSignedYen(noiDiff) : "-"
                      }
                      valueColor={
                        !dg.noiDiffAvailable
                          ? "#666"
                          : noiDiff >= 0
                            ? "#2E7D32"
                            : colors.status.error
                      }
                      loading={effectiveSummaryLoading}
                      loadingDelayMs={150}
                      icon={
                        dg.noiDiffAvailable && noiDiff < 0 ? (
                          <TrendingDownIcon
                            sx={{ fontSize: 14, color: colors.status.error }}
                          />
                        ) : undefined
                      }
                      sx={{
                        flex: 1,
                        p: 1.5,
                        ...(dg.noiDiffAvailable &&
                          noiDiff < 0 && { backgroundColor: "#FFF0F0" }),
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      border: "1px solid #E0E0E0",
                      mb: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#3E3E3E",
                        }}
                      >
                        NOI 予実推移
                      </Typography>
                      <ChartLegend />
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ mb: 1, mt: 1, flexWrap: "wrap" }}
                    >
                      {dg.properties.map((p) => (
                        <Button
                          key={p.property_id}
                          size="small"
                          variant={
                            drawerPropertyId === p.property_id
                              ? "contained"
                              : "text"
                          }
                          onClick={() => setDrawerPropertyId(p.property_id)}
                          disableElevation
                          sx={{
                            fontSize: "12px",
                            textTransform: "none",
                            borderRadius: "20px",
                            boxShadow: "none",
                            ...(drawerPropertyId === p.property_id
                              ? {
                                  backgroundColor: colors.primary.main,
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: colors.primary.main,
                                    boxShadow: "none",
                                  },
                                }
                              : {
                                  color: "#666",
                                  "&:hover": { boxShadow: "none" },
                                }),
                          }}
                        >
                          {p.name}
                        </Button>
                      ))}
                    </Stack>
                    {selectedProp && (
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <CustomChip
                          label={`実績 ${fmtZero(propNoiActual)}`}
                          fontSize="12px"
                          backgroundColor="#F5F5F5"
                          color={financialValueColor(propNoiActual)}
                          sx={{ borderRadius: "10px" }}
                        />
                        <CustomChip
                          label={`予算 ${fmtZero(propNoiBudget)}`}
                          fontSize="12px"
                          backgroundColor="#F5F5F5"
                          color={financialValueColor(propNoiBudget)}
                          sx={{ borderRadius: "10px" }}
                        />
                        <CustomChip
                          label={`差異 ${formatSignedYen(propNoiActual - propNoiBudget)}`}
                          fontSize="12px"
                          sx={{ borderRadius: "10px" }}
                          backgroundColor={
                            propNoiActual - propNoiBudget >= 0
                              ? "#E8F5E9"
                              : "#FFEBEE"
                          }
                          color={
                            propNoiActual - propNoiBudget >= 0
                              ? "#2E7D32"
                              : colors.status.error
                          }
                        />
                      </Stack>
                    )}
                    <Box sx={{ height: 240 }}>
                      {drawerMonthLabels.length > 0 &&
                        drawerMonthLabels.length === propBudgetNoi.length &&
                        drawerMonthLabels.length === propActualNoi.length && (
                          <LineChart
                            xAxis={[
                              { data: drawerMonthLabels, scaleType: "point" },
                            ]}
                            yAxis={[
                              {
                                valueFormatter: (v: number | null) =>
                                  fmtChart(v),
                              },
                            ]}
                            series={[
                              {
                                id: "drawer-budget",
                                data: propBudgetNoi,
                                label: "予算",
                                color: CHART_BUDGET_COLOR,
                                showMark: false,
                                valueFormatter: (v: number | null) =>
                                  fmtZero(v ?? 0),
                              },
                              {
                                id: "drawer-actual",
                                data: propActualNoi,
                                label: "実績",
                                color: CHART_ACTUAL_COLOR,
                                showMark: true,
                                // 予実分析は集計結果なので 0/未入力も 0 として全桁表示 (¥0)。
                                valueFormatter: (v: number | null) =>
                                  fmtZero(v ?? 0),
                              },
                            ]}
                            height={240}
                            slots={{ axisContent: ChartTooltipContent }}
                            slotProps={{ legend: { hidden: true } }}
                            sx={{
                              "& .MuiLineElement-series-drawer-budget": {
                                strokeDasharray: "6 4",
                              },
                              "& .MuiChartsAxisHighlight-root": {
                                stroke: CHART_HIGHLIGHT_COLOR,
                              },
                            }}
                          >
                            {currentMonthIndex >= 0 && (
                              <ChartsReferenceLine
                                x={drawerMonthLabels[currentMonthIndex]}
                                label="今月"
                                labelAlign="start"
                                lineStyle={{
                                  stroke: CHART_TODAY_COLOR,
                                  strokeDasharray: "4 3",
                                  strokeWidth: 1.5,
                                }}
                                labelStyle={{
                                  fontSize: 11,
                                  fill: CHART_TODAY_COLOR,
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </LineChart>
                        )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#3E3E3E",
                          }}
                        >
                          NOI 累計比較
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                          物件別の{fiscalYearLabel}累計 予算 vs 実績
                        </Typography>
                      </Box>
                      <ChartLegend />
                    </Stack>
                    <Box
                      sx={{ height: Math.max(propBarData.length * 50, 150) }}
                    >
                      <BarChart
                        yAxis={[
                          {
                            data: propBarData.map((d) => d.name),
                            scaleType: "band",
                          },
                        ]}
                        xAxis={[
                          {
                            valueFormatter: (v: number | null) => fmtChart(v),
                          },
                        ]}
                        series={[
                          {
                            data: propBarData.map((d) => d.budget),
                            label: "予算",
                            color: CHART_BUDGET_COLOR,
                          },
                          {
                            data: propBarData.map((d) => d.actual),
                            label: "実績",
                            color: CHART_ACTUAL_COLOR,
                          },
                        ]}
                        layout="horizontal"
                        height={Math.max(propBarData.length * 50, 150)}
                        margin={{ left: 170, right: 20, top: 10, bottom: 30 }}
                        slots={{ axisContent: ChartTooltipContent }}
                        slotProps={{ legend: { hidden: true } }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </PreviewDrawer>
          );
        })()}
    </Box>
  );
};

export default BudgetAnalysisPage;
